import {Map as ImmutableMap} from 'immutable';
import {createEisTypeObject, getKey} from 'Quoting/utils/utilities';
import {getCoverageDetailsObject} from './GWLUtilities';
import {ctlrMaxGIAmount} from './gpluConstants';

export const createNewPLPlan1ObjectWithDefault = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  defaultValues: ImmutableMap<string, any>
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    planCd: plan.planCode,
    planGroupsLinks: [],
    _type: 'GPLUMasterBenefitPlan',
    planName: plan.name,
    _key: key,
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory' || coverage.applicability === 'Optional')
      .map(coverage => getCoverageDetailsObject(coverage.code, defaultValues, rootId, parentId)),
    offerStatus: 'offered',
  };
};

export const getMoneyObject = (value: number) => ({_type: 'Money', amount: value, currency: 'USD'});

export const createBenefitPlanExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string
) => {
  return {
    ...createEisTypeObject('GPLUExclusions', rootId, parentId),
  };
};

export const createChildTLCoverageBenefitSchedule = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string
) => {
  return {
    offerStatus: 'offered',
    pctOfEmployeeAmount: {
      employeeAmountPct: defaultValues.get('GPLUBenefitSchedulePctOfEmployeeAmount.employeeAmountPct'),
      ...createEisTypeObject('GPLUBenefitSchedulePctOfEmployeeAmount', rootId, parentId),
    },
    benefitTypeCd: defaultValues.get('GPLUBenefitSchedule.benefitTypeCd') || 'RangeValues',
    rangeValues: createCTLRCoverageRangeValues(defaultValues, rootId, parentId),
    multipleValues: [],
    singleValue: undefined,
    ...createEisTypeObject('GPLUBenefitSchedule', rootId, parentId),
  };
};

export const createCTLRCoverageRangeValues = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  curGIAmount?: number
) => {
  return {
    benefitMinAmount: createAmountObject(defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMinAmount')),
    benefitMaxAmount: createAmountObject(getDefaultCTLRMaxBenefitAmount(defaultValues, curGIAmount) as any),
    incrementAmount: createAmountObject(defaultValues.get('GPLUBenefitScheduleRangeValues.incrementAmount')),
    ...createEisTypeObject('GPLUBenefitScheduleRangeValues', rootId, parentId),
  };
};

export const createCTLRCoverageSingleValue = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  curGIAmount?: number
) => {
  return {
    flatBenefitAmount: createAmountObject(getDefaultCTLRMaxGIAmount(defaultValues, curGIAmount) as any),
    ...createEisTypeObject('GPLUBenefitScheduleSingleValue', rootId, parentId),
  };
};

export const createWLCoverageBenefitSchedule = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string
) => {
  return {
    offerStatus: 'offered',
    benefitTypeCd: defaultValues.get('GPLUBenefitSchedule.benefitTypeCd') || 'RangeValues',
    minDeathBenefitAmountAllowance: createAmountObject(
      defaultValues.get('GPLUBenefitSchedule.minDeathBenefitAmountAllowance')
    ),
    maxDeathBenefitAmountAllowance: createAmountObject(
      defaultValues.get('GPLUBenefitSchedule.maxDeathBenefitAmountAllowance') ??
        createAmountObject(defaultValues.get('GPLUUnderwriting.guaranteedIssueAmount'))
    ),
    rangeValues: createRangeValues(defaultValues, rootId, parentId),
    multipleValues: [],
    singleValue: {
      ...createEisTypeObject('GPLUBenefitScheduleSingleValue', rootId, parentId),
    },
    benefitOptions: {
      changeFrequencyCd: defaultValues.get('GPLUBenefitScheduleBenefitOptions.changeFrequencyCd'),
      deathBenefitOptionCd: defaultValues.get('GPLUBenefitScheduleBenefitOptions.deathBenefitOptionCd'),
      ...createEisTypeObject('GPLUBenefitScheduleBenefitOptions', rootId, parentId),
    },
    ...createEisTypeObject('GPLUBenefitSchedule', rootId, parentId),
  };
};

export const createSpouseWLCoverageBenefitSchedule = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string
) => {
  return {
    offerStatus: 'offered',
    pctOfEmployeeAmount: {
      employeeAmountPct: defaultValues.get('GPLUBenefitSchedulePctOfEmployeeAmount.employeeAmountPct'),
      ...createEisTypeObject('GPLUBenefitSchedulePctOfEmployeeAmount', rootId, parentId),
    },
    multipleValues: [],
    ...createEisTypeObject('GPLUBenefitSchedule', rootId, parentId),
  };
};

export const createSpouseAccelDeathCoverageBenefitSchedule = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string
) => {
  return {
    benefitTypeLTCChronicCd: defaultValues.get('GPLUBenefitScheduleLTCChronic.benefitTypeLTCChronicCd'),
    inflationProtection: defaultValues.get('GPLUBenefitScheduleLTCChronic.inflationProtection'),
    maxBenefitTimePeriod: defaultValues.get('GPLUBenefitScheduleLTCChronic.maxBenefitTimePeriod'),
    monthlyDeathBenefitPct: defaultValues.get('GPLUBenefitScheduleLTCChronic.monthlyDeathBenefitPct'),
    ...createEisTypeObject('GPLUBenefitScheduleLTCChronic', rootId, parentId),
  };
};

const getdefaultMaxBenefitAmount = (type: string, defaultValues: ImmutableMap<string, any>, giAmount?: Number) => {
  const entityName = type === 'Ws' ? 'GPLUSpouseUnderwritingAmountsWs' : 'GPLUSpouseUnderwritingAmountsNws';
  const giAmountObj = defaultValues?.get(`${entityName}.guaranteedIssueAmount`);
  const primary = Number(giAmount);
  const fallback = giAmountObj ? Number(giAmountObj.get('amount')) : 0;
  const amount = Number.isNaN(primary) ? fallback : Math.min(primary, fallback);
  return ImmutableMap({_type: 'Money', amount: amount, currency: 'USD'});
};

export const createSpouseWLCoverageRangeValues = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  type: string,
  giAmount?: number
) => {
  const rangeValues = {
    benefitMinAmount: createAmountObject(defaultValues.get(`GPLUBenefitScheduleRangeValues${type}.benefitMinAmount`)),
    benefitMaxAmount: createAmountObject(getdefaultMaxBenefitAmount(type, defaultValues, giAmount) as any),
    incrementAmount: createAmountObject(defaultValues.get(`GPLUBenefitScheduleRangeValues${type}.incrementAmount`)),
    ...createEisTypeObject(`GPLUBenefitScheduleRangeValues${type}`, rootId, parentId),
  };
  return rangeValues;
};

export const createSpouseWLCoverageSingleValue = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  type: string,
  giAmount?: number
) => {
  return {
    ...createEisTypeObject('GPLUBenefitScheduleSingleValue'.concat(type), rootId, parentId),
    flatBenefitAmount: createAmountObject(getdefaultMaxBenefitAmount(type, defaultValues, giAmount) as any),
  };
};

export const createSpouseWLCoverageBenefitSchedules = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  type: string,
  giAmount?: number
) => {
  return {
    offerStatus: 'offered',
    [`benefitType${type}Cd`]: defaultValues.get(`GPLUBenefitSchedule${type}.benefitType${type}Cd`),
    spouseType: type.toUpperCase(),
    rangeValues: createSpouseWLCoverageRangeValues(defaultValues, rootId, parentId, type, giAmount),
    minDeathBenefitAllowance: createAmountObject(
      defaultValues.get(`GPLUBenefitScheduleRangeValues${type}.benefitMinAmount`)
    ),
    maxDeathBenefitAllowance: createAmountObject(getdefaultMaxBenefitAmount(type, defaultValues, giAmount) as any),
    multipleValues: [],
    singleValue: undefined,
    benefitOptions: {
      changeFrequencyCd: defaultValues.get(`GPLUBenefitScheduleBenefitOptions${type}.changeFrequencyCd`),
      deathBenefitOptionCd: defaultValues.get(`GPLUBenefitScheduleBenefitOptions${type}.deathBenefitOptionCd`),
      ...createEisTypeObject(`GPLUBenefitScheduleBenefitOptions`.concat(type), rootId, parentId),
    },
    ...createEisTypeObject(`GPLUBenefitSchedule`.concat(type), rootId, parentId),
  };
};

export const createSpouseUnderwritingAmounts = (
  defaultValues: ImmutableMap<string, any>,
  type: string,
  rootId: string,
  parentId: string,
  giAmount?: number
) => {
  const spouseType = type === 'GPLUSpouseUnderwritingAmountsWs' ? 'Ws' : 'Nws';
  return {
    ...createEisTypeObject(type, rootId, parentId),
    guaranteedIssueAmount: createAmountObject(getdefaultMaxBenefitAmount(spouseType, defaultValues, giAmount) as any),
    maxUnderWritingLimitAllowance: createAmountObject(
      getdefaultMaxBenefitAmount(spouseType, defaultValues, giAmount) as any
    ),
  };
};

export const createBenefitPlanAccelExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string
) => {
  return {
    ...createEisTypeObject('GPLUExclusions', rootId, parentId),
    hasMentalOrEmotionalDisorder: defaultValues.get('GPLUExclusions.hasMentalOrEmotionalDisorder'),
    isForeignTreatment: defaultValues.get('GPLUExclusions.isForeignTreatment'),
    isSuicideOrSelfInflictedInjury: defaultValues.get('GPLUExclusions.isSuicideOrSelfInflictedInjury'),
    hasAlcoholOrDrugAddiction: defaultValues.get('GPLUExclusions.hasAlcoholOrDrugAddiction'),
    isServicePayableUnderGovtProgram: defaultValues.get('GPLUExclusions.isServicePayableUnderGovtProgram'),
    isWarIllness: defaultValues.get('GPLUExclusions.isWarIllness'),
    isCoveredUnderWorkersCompOrNoFaultLaw: defaultValues.get('GPLUExclusions.isCoveredUnderWorkersCompOrNoFaultLaw'),
  };
};

export const createGPLUEligibilities = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  eligibility: string
) => {
  return {
    maxIssueAge: defaultValues.get(`GPLUEligibility${eligibility}.maxIssueAge`),
    minIssueAge: defaultValues.get(`GPLUEligibility${eligibility}.minIssueAge`),
    minHourlyReq: eligibility === '' ? defaultValues.get('GPLUEligibility.minHourlyReq') : null,
    ...createEisTypeObject('GPLUEligibility'.concat(eligibility), rootId, parentId),
  };
};

export const createAmountObject = (
  defaultValue: ImmutableMap<string, {_type: 'Money'; amount: number; currency: 'USD'}>
) => (ImmutableMap.isMap(defaultValue) ? defaultValue.set('_type', undefined) : undefined);

export const getDefaultCTLRMaxGIAmount = (defaultValues: ImmutableMap<string, any>, curGIAmount?: number) => {
  const giAmountObj = defaultValues?.get('GPLUUnderwriting.guaranteedIssueAmount');
  const primary = Number(curGIAmount);
  const fallback = giAmountObj ? Number(giAmountObj.get('amount')) : 0;
  const giAmount = !Number.isNaN(primary) ? primary : fallback;
  return ImmutableMap({_type: 'Money', amount: Math.min(giAmount, ctlrMaxGIAmount), currency: 'USD'});
};

export const getDefaultCTLRMaxBenefitAmount = (defaultValues: ImmutableMap<string, any>, curGIAmount?: number) => {
  const giAmountObj =
    defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMaxAmount') ??
    defaultValues.get('GPLUUnderwriting.guaranteedIssueAmount');
  const primary = Number(curGIAmount);
  const fallback = giAmountObj ? Number(giAmountObj.get('amount')) : 0;
  const giAmount = !Number.isNaN(primary) ? primary : fallback;
  return ImmutableMap({_type: 'Money', amount: Math.min(giAmount, ctlrMaxGIAmount), currency: 'USD'});
};

export const createRangeValues = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  uwAmount?: any
) => ({
  benefitMaxAmount: uwAmount
    ? createAmountObject(uwAmount)
    : createAmountObject(defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMaxAmount')) ??
      createAmountObject(defaultValues.get('GPLUUnderwriting.guaranteedIssueAmount')),
  benefitMinAmount: createAmountObject(defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMinAmount')),
  incrementAmount: createAmountObject(defaultValues.get('GPLUBenefitScheduleRangeValues.incrementAmount')),
  ...createEisTypeObject('GPLUBenefitScheduleRangeValues', rootId, parentId),
});

export const createSingleValue = (
  rootId: string,
  parentId: string,
  uwAmount: any | undefined,
  defaultValues: ImmutableMap<string, any>
) => ({
  flatBenefitAmount: uwAmount ?? createAmountObject(defaultValues.get(`GPLUUnderwriting.guaranteedIssueAmount`)),
  ...createEisTypeObject('GPLUBenefitScheduleSingleValue', rootId, parentId),
});
